import { vec3 } from 'gl-matrix';
import {
  FloorTagged,
  SegmentHit,
  SpatialQueryGrid,
  VolumeHeightSample,
  VolumeLeafCallback,
} from '../SpatialQueryGrid';
import { Tlas, raycastTlas, segmentHitTlas } from './Tlas';

/**
 * Heightmap fallback signature (same shape as TiledBvh's). Used when
 * the BVH has no hit at a given query — typically for tiles outside the
 * loaded ring or for queries that go through "empty space" in the sky.
 */
export type TerrainFallback = (x: number, z: number) => VolumeHeightSample | null;

const PROBE_FAR = 10000; // meters; effectively unbounded for terrain-scale queries.
/**
 * "Is there a triangle within this distance in any axis direction?" — matches
 * `PhysicsFeature`'s usage pattern of probing `(x + voxelSize * sign(vel), y, z)`
 * to detect wall normal. We treat any of the 6 axis-aligned short rays hitting
 * within this distance as "solid".
 */
const ISSOLID_PROBE_M = 0.15;

// Module-scoped scratch vectors reused across query calls. JS is single-threaded
// so there's no reentrancy concern. The 6 axis directions for `isSolidAt` are
// constants that don't need rebuilding each call.
const SCRATCH_ORIGIN = vec3.create();
const SCRATCH_DIR_DOWN = vec3.fromValues(0, -1, 0);
const SCRATCH_DIR_UP = vec3.fromValues(0, 1, 0);
const ISSOLID_DIRS: ReadonlyArray<vec3> = [
  vec3.fromValues(1, 0, 0),
  vec3.fromValues(-1, 0, 0),
  vec3.fromValues(0, 1, 0),
  vec3.fromValues(0, -1, 0),
  vec3.fromValues(0, 0, 1),
  vec3.fromValues(0, 0, -1),
];
const SCRATCH_RAY_ORIGIN = vec3.create();
const SCRATCH_RAY_DIR = vec3.create();

/**
 * Adapter that exposes a per-tile `Tlas` through the existing `SpatialQueryGrid`
 * contract. Every method maps to one or two BVH ray queries:
 *
 *   - Floor / ceiling / height queries → vertical ray (downward or upward)
 *   - rayCrossesSolid → segment test
 *   - isSolidAt → small downward + upward probes (this is the awkward one;
 *     the only consumer is PhysicsFeature's wall-normal derivation, which can
 *     be refactored later to use the raycast normal directly)
 *   - hasTileAt → "TLAS has any leaves"
 *   - forEachLeaf → iterate TLAS leaves, emit one rectangle per instance
 *
 * Terrain-origin tagging: TLAS leaves carry `isTerrain: boolean`. When the
 * nearest hit comes from a terrain leaf, `FloorTagged.terrainOrigin = true`
 * — same semantics as the volgrid's `lowestSolidIsTerrain` bit, so the
 * tiled layer's terrain-fallback substitution works unchanged.
 */
export class BvhSpatialGrid implements SpatialQueryGrid {
  /** Nominal value matching the volgrid's default; some callers use it as a step size. */
  public voxelSize: number = 0.1;

  private terrainFallback: TerrainFallback | null = null;

  constructor(public readonly tlas: Tlas) {}

  setTerrainFallback(fn: TerrainFallback | null): void {
    this.terrainFallback = fn;
  }

  // --- Internal helpers ---

  /** Downward ray from `(x, y, z)`. Returns nearest hit or null. */
  private rayDown(x: number, y: number, z: number, maxDist: number = PROBE_FAR) {
    SCRATCH_ORIGIN[0] = x; SCRATCH_ORIGIN[1] = y; SCRATCH_ORIGIN[2] = z;
    return raycastTlas(this.tlas, SCRATCH_ORIGIN, SCRATCH_DIR_DOWN, maxDist);
  }

  /** Upward ray from `(x, y, z)`. Returns nearest hit or null. */
  private rayUp(x: number, y: number, z: number, maxDist: number = PROBE_FAR) {
    SCRATCH_ORIGIN[0] = x; SCRATCH_ORIGIN[1] = y; SCRATCH_ORIGIN[2] = z;
    return raycastTlas(this.tlas, SCRATCH_ORIGIN, SCRATCH_DIR_UP, maxDist);
  }

  // --- SpatialQueryGrid implementation ---

  /**
   * "Is there solid geometry adjacent to this point?" For a triangle BVH this
   * is ill-defined on open meshes — we approximate with 6 short axis-aligned
   * rays. Returns true if any of them hit a triangle within ISSOLID_PROBE_M.
   * Matches `PhysicsFeature`'s usage of stepping `voxelSize` in a velocity
   * direction and asking "is that next position blocked?". Refactoring
   * `PhysicsFeature` to use the raycast normal directly would let us drop
   * this entirely.
   */
  isSolidAt(x: number, y: number, z: number): boolean {
    SCRATCH_ORIGIN[0] = x; SCRATCH_ORIGIN[1] = y; SCRATCH_ORIGIN[2] = z;
    for (let i = 0; i < ISSOLID_DIRS.length; i++) {
      if (raycastTlas(this.tlas, SCRATCH_ORIGIN, ISSOLID_DIRS[i], ISSOLID_PROBE_M) !== null) {
        return true;
      }
    }
    return false;
  }

  getFloorHeight(x: number, y: number, z: number): number | null {
    return this.getFloorHeightTagged(x, y, z)?.y ?? null;
  }

  getFloorHeightTagged(x: number, y: number, z: number): FloorTagged | null {
    return this.getFloorHeightBelowTagged(x, y, z);
  }

  getCeilingHeight(x: number, y: number, z: number): number | null {
    return this.getCeilingHeightAbove(x, y, z);
  }

  getCeilingHeightAbove(x: number, y: number, z: number): number | null {
    const hit = this.rayUp(x, y, z);
    return hit !== null ? y + hit.t : null;
  }

  getFloorHeightBelow(x: number, y: number, z: number): number | null {
    return this.getFloorHeightBelowTagged(x, y, z)?.y ?? null;
  }

  getFloorHeightBelowTagged(x: number, y: number, z: number): FloorTagged | null {
    const hit = this.rayDown(x, y, z);
    if (hit !== null) {
      return { y: y - hit.t, terrainOrigin: hit.isTerrain };
    }
    const fb = this.terrainFallback?.(x, z);
    if (fb !== null && fb !== undefined) {
      return { y: fb.height, terrainOrigin: true };
    }
    return null;
  }

  getFloorHeightBelowSmooth(x: number, y: number, z: number): number | null {
    // 4 corner samples at half-voxel offsets, bilinear blend.
    const vs = this.voxelSize;
    const half = vs * 0.5;
    const a = this.getFloorHeightBelow(x - half, y, z - half);
    const b = this.getFloorHeightBelow(x + half, y, z - half);
    const c = this.getFloorHeightBelow(x - half, y, z + half);
    const d = this.getFloorHeightBelow(x + half, y, z + half);
    let sum = 0;
    let count = 0;
    if (a !== null) { sum += a; count++; }
    if (b !== null) { sum += b; count++; }
    if (c !== null) { sum += c; count++; }
    if (d !== null) { sum += d; count++; }
    return count > 0 ? sum / count : null;
  }

  getFloorHeightWithNormal(x: number, y: number, z: number): VolumeHeightSample | null {
    const hit = this.rayDown(x, y, z);
    if (hit !== null) {
      // For terrain-origin hits, prefer the heightmap fallback's smoother
      // normal (matches volgrid behavior — the tiled layer substitutes
      // terrain normals for terrain-origin hits in mixed columns).
      if (hit.isTerrain) {
        const fb = this.terrainFallback?.(x, z);
        if (fb !== null && fb !== undefined) return fb;
      }
      // Normal points outward from the surface; flip if it points downward
      // (we're standing on top of the triangle, want normal pointing up).
      const n = hit.normal;
      const ny = n[1] >= 0 ? n : vec3.fromValues(-n[0], -n[1], -n[2]);
      return { height: y - hit.t, normal: ny };
    }
    const fb = this.terrainFallback?.(x, z);
    return fb ?? null;
  }

  rayCrossesSolid(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): boolean {
    SCRATCH_RAY_ORIGIN[0] = x0; SCRATCH_RAY_ORIGIN[1] = y0; SCRATCH_RAY_ORIGIN[2] = z0;
    SCRATCH_RAY_DIR[0] = x1; SCRATCH_RAY_DIR[1] = y1; SCRATCH_RAY_DIR[2] = z1;
    return segmentHitTlas(this.tlas, SCRATCH_RAY_ORIGIN, SCRATCH_RAY_DIR);
  }

  /**
   * BVH-native: cast a ray from p0 along (p1 - p0) and return the t (∈[0,1])
   * of the first triangle hit, or null if the segment is clear. Faster and
   * sub-voxel-accurate compared to the bisection fallback in volgrid.
   */
  segmentHitDistance(
    x0: number, y0: number, z0: number,
    x1: number, y1: number, z1: number,
  ): number | null {
    SCRATCH_RAY_ORIGIN[0] = x0; SCRATCH_RAY_ORIGIN[1] = y0; SCRATCH_RAY_ORIGIN[2] = z0;
    SCRATCH_RAY_DIR[0] = x1 - x0; SCRATCH_RAY_DIR[1] = y1 - y0; SCRATCH_RAY_DIR[2] = z1 - z0;
    const hit = raycastTlas(this.tlas, SCRATCH_RAY_ORIGIN, SCRATCH_RAY_DIR, 1.0);
    return hit !== null ? hit.t : null;
  }

  /**
   * BVH-native: returns the first hit's parametric distance + exact triangle
   * face normal + terrain-origin flag. Unlike the volgrid fallback, this is
   * a real surface normal (the geometric normal of the hit triangle, world-
   * space, oriented away from the ray's incoming direction) — projectile
   * bounces against it are physically correct.
   */
  segmentHitWithNormal(
    x0: number, y0: number, z0: number,
    x1: number, y1: number, z1: number,
  ): SegmentHit | null {
    SCRATCH_RAY_ORIGIN[0] = x0; SCRATCH_RAY_ORIGIN[1] = y0; SCRATCH_RAY_ORIGIN[2] = z0;
    SCRATCH_RAY_DIR[0] = x1 - x0; SCRATCH_RAY_DIR[1] = y1 - y0; SCRATCH_RAY_DIR[2] = z1 - z0;
    const hit = raycastTlas(this.tlas, SCRATCH_RAY_ORIGIN, SCRATCH_RAY_DIR, 1.0);
    if (hit === null) return null;
    // Make sure the normal opposes the ray direction (so reflections work
    // regardless of which side of the triangle we hit). The raycast hands us
    // a face normal whose sign depends on the triangle's winding.
    const dot = hit.normal[0] * SCRATCH_RAY_DIR[0] +
                hit.normal[1] * SCRATCH_RAY_DIR[1] +
                hit.normal[2] * SCRATCH_RAY_DIR[2];
    const normal = dot > 0
      ? vec3.fromValues(-hit.normal[0], -hit.normal[1], -hit.normal[2])
      : hit.normal;
    return { t: hit.t, normal, isTerrain: hit.isTerrain };
  }

  hasTileAt(_x: number, _z: number): boolean {
    return this.tlas.leaves.length > 0;
  }

  /**
   * Debug viz hook — iterate TLAS leaves, emit one synthetic rectangle per
   * instance using its world AABB. Lossy translation (BVH leaves are
   * instance-AABBs, not flat/complex quadtree leaves) but the only consumer
   * is `VisualSpatialGrid` which uses it for wireframe debug viz.
   */
  forEachLeaf(callback: VolumeLeafCallback): void {
    const bounds = this.tlas.nodeBounds;
    const childCount = this.tlas.nodeChildCount;
    for (let i = 0; i < this.tlas.nodeCount; i++) {
      if (childCount[i] === 0) continue; // internal
      const b = i * 6;
      // Use NODE_COMPLEX (2) for all BVH leaves — closest analogue.
      callback(
        bounds[b], bounds[b + 2],
        bounds[b + 3] - bounds[b], bounds[b + 5] - bounds[b + 2],
        2, // NODE_COMPLEX
        i, // node offset proxy
        0, // depth — unused here
      );
    }
  }
}
